import { Reference, ReferenceProperty } from '../reference/Reference.interface';

// jira

const JiraUser: Reference = {
  name: 'Jira Issue Status',
  typeName: 'JiraIssueStatus',
  props: [
    { id: 'accountId', name: 'Account ID', type: 'string' },
    { id: 'displayName', name: 'Display Name', type: 'string' }
  ]
};

const JiraStatus: Reference = {
  name: 'Jira Status',
  typeName: 'JiraStatus',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'name', name: 'Name', type: 'string' }
  ]
};

const JiraIssue: Reference = {
  name: 'Jira Issue',
  typeName: 'JiraIssue',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'key', name: 'Key', type: 'string' },
    { id: 'summary', name: 'Summary', type: 'string' },
    { id: 'status', name: 'Status', type: JiraStatus },
    { id: 'assignee', name: 'Assignee', type: JiraUser }
  ]
};

// xray common

const XrayTestType: Reference = {
  name: 'Xray Test Type',
  typeName: 'XrayTestType',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'name', name: 'Name', type: 'string' }
  ]
};

const XrayStatus: Reference = {
  name: 'Xray Status',
  typeName: 'XrayStatus',
  props: [
    { id: 'name', name: 'Name', type: 'string' },
    { id: 'description', name: 'Description', type: 'string' },
    { id: 'final', name: 'Final', type: 'boolean' },
    {
      id: 'color',
      name: 'Color',
      type: 'string',
      description: 'Hex color string.'
    }
  ]
};

const XrayPaginatedListProps: ReferenceProperty[] = [
  { id: 'total', name: 'Total', type: 'number' },
  { id: 'start', name: 'Start', type: 'number' },
  { id: 'limit', name: 'Limit', type: 'number' }
];

const XrayEntityCommonProps: ReferenceProperty[] = [
  { id: 'issueId', name: 'Jira Issue ID', type: 'string' },
  { id: 'projectId', name: 'Jira Project ID', type: 'string' },
  { id: 'jira', name: 'Jira Issue', type: JiraIssue }
];

// xray test

const XrayTest: Reference = {
  name: 'Xray Test',
  typeName: 'XrayTest',
  props: [
    ...XrayEntityCommonProps,
    { id: 'testType', name: 'Xray Test Type', type: XrayTestType },
    { id: 'status', name: 'Xray Status', type: XrayStatus }
  ]
};

const XrayTestResults: Reference = {
  name: 'Xray Test Results',
  typeName: 'XrayTestResults',
  description: 'Paginated list of Xray Tests',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayTest }
  ]
};

const XrayPrecondition: Reference = {
  name: 'Xray Precondition',
  typeName: 'XrayPrecondition',
  props: [
    ...XrayEntityCommonProps,
    { id: 'definition', name: 'Definition', type: 'string' }
  ]
};

const XrayPreconditionDetailed: Reference = {
  name: 'Xray Precondition (Detailed)',
  typeName: 'XrayPreconditionDetailed',
  props: [
    ...XrayPrecondition.props,
    { id: 'tests', name: 'Xray Test Results', type: XrayTestResults }
  ]
};

const XrayPreconditionResults: Reference = {
  name: 'Xray Precondition Results',
  typeName: 'XrayPreconditionResults',
  description: 'Paginated list of Xray Preconditions',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayPrecondition }
  ]
};

const XrayTestSet: Reference = {
  name: 'Xray Test Set',
  typeName: 'XrayTestSet',
  props: XrayEntityCommonProps
};

const XrayTestSetDetailed: Reference = {
  name: 'Xray Test Set (Detailed)',
  typeName: 'XrayTestSetDetailed',
  props: [
    ...XrayTestSet.props,
    { id: 'tests', name: 'Xray Test Results', type: XrayTestResults }
  ]
};

const XrayTestSetResults: Reference = {
  name: 'Xray Test Set Results',
  typeName: 'XrayTestSetResults',
  description: 'Paginated list of Xray Test Sets',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayTestSet }
  ]
};

const XrayTestPlan: Reference = {
  name: 'Xray Test Plan',
  typeName: 'XrayTestPlan',
  props: XrayEntityCommonProps
};

const XrayTestExecution: Reference = {
  name: 'Xray Test Execution',
  typeName: 'XrayTestExecution',
  props: XrayEntityCommonProps
};

const XrayTestExecutionResults: Reference = {
  name: 'Xray Test Execution Results',
  typeName: 'XrayTestExecutionResults',
  description: 'Paginated list of Xray Test Executions',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayTestExecution }
  ]
};

const XrayTestPlanDetailed: Reference = {
  name: 'Xray Test Plan (Detailed)',
  typeName: 'XrayTestPlanDetailed',
  props: [
    ...XrayTestPlan.props,
    { id: 'tests', name: 'Xray Test Results', type: XrayTestResults },
    {
      id: 'testExecutions',
      name: 'Xray Test Execution Results',
      type: XrayTestExecutionResults
    }
  ]
};

const XrayTestPlanResults: Reference = {
  name: 'Xray Test Plan Results',
  typeName: 'XrayTestPlanResults',
  description: 'Paginated list of Xray Test Plans',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayTestPlan }
  ]
};

const XrayTestExecutionDetailed: Reference = {
  name: 'Xray Test Execution (Detailed)',
  typeName: 'XrayTestExecutionDetailed',
  props: [
    ...XrayTestExecution.props,
    { id: 'tests', name: 'Xray Test Results', type: XrayTestResults }
  ]
};

const XrayTestRun: Reference = {
  name: 'Xray Test Run',
  typeName: 'XrayTestRun',
  props: [
    { id: 'id', name: 'ID', type: 'string' },
    { id: 'status', name: 'Status', type: XrayStatus },
    {
      id: 'textExecution',
      name: 'Xray Test Execution',
      type: XrayTestExecution
    }
  ]
};

const XrayTestRunResults: Reference = {
  name: 'Xray Test Run Results',
  typeName: 'XrayTestRunResults',
  description: 'Paginated list of Xray Test Run Results',
  props: [
    ...XrayPaginatedListProps,
    { id: 'results', name: 'Results', isArray: true, type: XrayTestRun }
  ]
};

const XrayTestDetailed: Reference = {
  name: 'Xray Test (Detailed)',
  typeName: 'XrayTestDetailed',
  props: [
    ...XrayTest.props,
    {
      id: 'preconditions',
      name: 'Xray Preconditions',
      type: XrayPreconditionResults
    },
    { id: 'testSets', name: 'Xray Test Sets', type: XrayTestSetResults },
    { id: 'testPlans', name: 'Xray Test Plans', type: XrayTestPlanResults },
    {
      id: 'testExecutions',
      name: 'Xray Test Executions',
      type: XrayTestExecutionResults
    },
    { id: 'testRuns', name: 'Xray Test Runs', type: XrayTestRunResults }
  ]
};

export const XrayJiraIssueData: Reference = {
  name: 'Xray Jira Issue Data',
  typeName: 'XrayJiraIssueData',
  props: [
    {
      id: 'test',
      name: 'Xray Test (Detailed)',
      description:
        'Optional. Only available if the issue type of the Jira issue matches the mapped "Test" Xray issue type.',
      type: XrayTestDetailed
    },
    {
      id: 'precondition',
      name: 'Xray Precondition (Detailed)',
      description:
        'Optional. Only available if the issue type of the Jira issue matches the mapped "Precondition" Xray issue type.',
      type: XrayPreconditionDetailed
    },
    {
      id: 'testSet',
      name: 'Xray Test Set (Detailed)',
      description:
        'Optional. Only available if the issue type of the Jira issue matches the mapped "Test Set" Xray issue type.',
      type: XrayTestSetDetailed
    },
    {
      id: 'testPlan',
      name: 'Xray Test Plan (Detailed)',
      description:
        'Optional. Only available if the issue type of the Jira issue matches the mapped "Test Plan" Xray issue type.',
      type: XrayTestPlanDetailed
    },
    {
      id: 'testExecution',
      name: 'Xray Test Execution (Detailed)',
      description:
        'Optional. Only available if the issue type of the Jira issue matches the mapped "Test Execution" Xray issue type.',
      type: XrayTestExecutionDetailed
    }
  ]
};
